import { refreshAccessToken } from './tokenRefresh';
import { SUBSCRIPTION_HEADER_NAME } from '../constants/constants';
import { API_ROOT } from './apiConfig';

// eslint-disable-next-line no-undef
export const CALL_API = "Call API";

export default store => next => action => {
    const callAPI = action[CALL_API];
    if (typeof callAPI === "undefined") {
        return next(action);
    }

    let { endpoint, method, body, headers = {}, signal = null } = callAPI;
    const { types } = callAPI;

    headers.Pragma = "no-cache";

    if (endpoint === 'logout') {
        // Fake logout call to fake out redux-refresh-token and return the promise
        // that it is expecting.
        return Promise.resolve();
    }

    const token = store.getState().user.accessToken;
    if (typeof token === "string") {
        headers.Authorization = `Bearer ${token}`;
    }

    const subscriptionId = store.getState().user.currentSubscriptionId;
    if (subscriptionId) {
        headers[SUBSCRIPTION_HEADER_NAME] = subscriptionId;
    }

    const actionWith = data => {
        const finalAction = Object.assign({}, action, data);
        delete finalAction[CALL_API];
        return finalAction;
    };

    const [requestType, successType, failureType] = types;
    next(actionWith({ type: requestType }));

    // Only stringify the body if we're not dealing with a multipart 
    // FormData object
    if (body && Object.prototype.toString.call(body) !== "[object FormData]") {
        if (endpoint !== 'account/refresh-token') { // Refresh token would return 415
            headers['Content-Type'] = 'application/json';
        }
        body = JSON.stringify(body);
    }

    return (
        fetch(`${API_ROOT}${endpoint}`, {
            method,
            body,
            headers,
            credentials: 'include',
            signal
        })
        // Reads the body stream into Flux Standard Action
        .then(createFSAConverter(successType, failureType))
        .then(async (result) => {
            const isRefreshCall = endpoint === 'account/refresh-token';
            const is401 = !!(result && result.error && result.payload && result.payload.status === 401);
            // Login / forgot-password etc. carry no token and must surface their own errors.
            const startedAuthenticated = typeof token === 'string' && token.length > 0;

            if (is401 && !isRefreshCall && startedAuthenticated) {
                // Let the failure action reach the reducer, exactly as before.
                next(result);

                /**
                 * Shared single-flight refresh. If the tanstack/ky stack (or another
                 * redux request) is already refreshing, this awaits that same POST
                 * instead of firing a second one with the same one-time-use refresh
                 * token — which is what was logging valid sessions out at random.
                 */
                const newToken = await refreshAccessToken(store);

                if (newToken && !action.__authRetried) {
                    // Retry the original request once, now that the store holds a fresh token.
                    action.__authRetried = true;
                    return store.dispatch(action);
                }

                /**
                 * Either the refresh failed (session over — refreshAccessToken already
                 * dispatched LOGOUT_SUCCESS; resetEnhancer cleared redux and PrivateRoute
                 * redirects to login) or we already retried once. Suppress so no stale
                 * toast/crash flashes during the redirect.
                 */
                if (!newToken) {
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    return new Promise(() => {});
                }
                return result;
            }

            return next(result);
        })
    );
};

/**
 * In order to add a server-side error message to the Flux-Standard Action error
 * response, we have taken the implementation of createFSAConverter from the
 * redux-refresh-token package and defined it here instead of importing the one
 * in the package
 */
const createFSAConverter = (successType, failureType) => {
    return async (response) => {

        if (!response.ok) {
            try {
                const errorBody = await response.json();
                return {
                    error: true,
                    payload: {
                        ...errorBody,
                        status: response.status,
                        message: (errorBody && errorBody.message) ? errorBody.message : 'An error has occurred',
                    },
                    type: failureType
                }
            } catch (error) {
                return {
                    error: true,
                    payload: {
                        status: response.status,
                        message: 'An error has occurred'
                    },
                    type: failureType
                }
            }
        }

        const contentType = response.headers.get("Content-Type");
        const emptyCodes = [204, 205];

        const createSuccessType = payload => ({
            payload,
            type: successType
        })

        if (
            emptyCodes.indexOf(response.status) === -1 &&
            contentType &&
            contentType.indexOf("json") !== -1
        ) {
            return response.json().then(createSuccessType);
        } else {
            return createSuccessType();
        }
    }
}
